/**
 * SoleadLab 2.0 - Task Store
 *
 * Manages the issue-to-task workflow state. Issues are created from
 * thermal indications or monitoring alarms, then converted into
 * actionable maintenance tasks with scheduling and assignment.
 *
 * V4 Write Switch: When _entityMigrated is true, all mutations are
 * written to the entity API with optimistic concurrency.
 * Non-migrated sites remain local-only (blob persistence).
 *
 * State groups:
 * - Issues: inbox of detected problems (from indications/alarms)
 * - Tasks: actionable work items created from issues
 * - Audit log: global action history
 * - UI state: selected issue, active tab, filters, batch mode
 */

import { create } from 'zustand';
import type {
  Issue,
  Task,
  AuditEntry,
  TasksConfig,
  TasksTabId,
  IssueFilterId,
  IssueState,
  IssueSeverity,
  TaskPriority,
  TaskStatus,
  EstimatedImpact,
} from '../types/tasks';
import {
  INDICATION_SEVERITY,
  SEVERITY_PRIORITY,
  TYPE_CATEGORY,
  RECOMMENDED_ACTIONS,
} from '../types/tasks';
import type { Indication } from './projectStore';
import { useMonitoringStore } from './monitoringStore';
import type { DataPointConfig, TriggeredAdvancedRule } from '../types/monitoring';
import {
  createEntityIssue,
  updateEntityIssue,
  deleteEntityIssue,
  createEntityTask,
  updateEntityTask,
  fetchSingleEntityIssue,
  fetchSingleEntityTask,
  isConflictError,
} from '../services/taskEntityApi';

// ============================================
// Helpers
// ============================================

function uuid(): string {
  return crypto.randomUUID();
}

function now(): string {
  return new Date().toISOString();
}

/**
 * Issue category label for an indication. For the user-defined 'Custom' type the
 * custom name (properties.customTypeName) is used so the resulting issue/task
 * reads as e.g. "Fence damage" instead of the generic "Other".
 */
function indicationCategory(ind: Indication): string {
  if (ind.type === 'Custom') {
    const name = ind.properties.customTypeName?.trim();
    if (name) return name;
  }
  return TYPE_CATEGORY[ind.type] ?? 'Other';
}

function centroid(geometry: GeoJSON.Geometry): { lng: number; lat: number } {
  if (geometry.type === 'Point') {
    const [lng, lat] = geometry.coordinates as [number, number];
    return { lng, lat };
  }
  if (geometry.type === 'Polygon') {
    const coords = (geometry.coordinates as number[][][])[0];
    let sumLng = 0, sumLat = 0;
    for (const [lng, lat] of coords) {
      sumLng += lng;
      sumLat += lat;
    }
    return { lng: sumLng / coords.length, lat: sumLat / coords.length };
  }
  return { lng: 0, lat: 0 };
}

// ============================================
// V4 Pending Write Guards
// ============================================
// Tracks entity IDs with unconfirmed API writes.
// Value = pre-mutation snapshot (null for pending adds).
// getConfirmedIssues/Tasks() guard getTasksConfig() so blob
// autosave never serializes unconfirmed optimistic mutations.

const pendingIssueWrites = new Map<string, Issue | null>();
const pendingTaskWrites = new Map<string, Task | null>();

function getConfirmedIssues(issues: Issue[]): Issue[] {
  if (pendingIssueWrites.size === 0) return issues;
  const result: Issue[] = [];
  for (const iss of issues) {
    if (!pendingIssueWrites.has(iss.issueId)) {
      result.push(iss);
    } else {
      const snapshot = pendingIssueWrites.get(iss.issueId) ?? null;
      if (snapshot === null) continue; // pending add — exclude
      result.push(snapshot); // pending update — use pre-mutation snapshot
    }
  }
  for (const [, snapshot] of pendingIssueWrites) {
    if (snapshot !== null && snapshot !== undefined && !result.some((i) => i.issueId === snapshot.issueId)) {
      result.push(snapshot); // pending delete — re-include
    }
  }
  return result;
}

function getConfirmedTasks(tasks: Task[]): Task[] {
  if (pendingTaskWrites.size === 0) return tasks;
  const result: Task[] = [];
  for (const tsk of tasks) {
    if (!pendingTaskWrites.has(tsk.taskId)) {
      result.push(tsk);
    } else {
      const snapshot = pendingTaskWrites.get(tsk.taskId) ?? null;
      if (snapshot === null) continue;
      result.push(snapshot);
    }
  }
  for (const [, snapshot] of pendingTaskWrites) {
    if (snapshot !== null && snapshot !== undefined && !result.some((t) => t.taskId === snapshot.taskId)) {
      result.push(snapshot);
    }
  }
  return result;
}

// ============================================
// Store Interface
// ============================================

interface TaskState {
  // Data
  issues: Issue[];
  tasks: Task[];
  auditLog: AuditEntry[];

  // Entity authority (V4 write switch)
  _entitySiteId: string | null;
  _entityMigrated: boolean;

  // UI state
  selectedIssueId: string | null;
  selectedTaskId: string | null;
  activeTab: TasksTabId;
  issueFilter: IssueFilterId;
  batchMode: boolean;
  selectedTaskIds: Set<string>;

  // Entity authority
  setEntityAuthority: (siteId: string | null, migrated: boolean) => void;

  // Issue actions
  syncFromIndications: (indications: Indication[], siteId: string) => number;
  syncOrRemoveIssueForIndication: (indication: Indication, siteId: string, createIssue: boolean) => void;
  syncFromAlarms: () => number;
  addIssue: (issue: Issue) => void;
  updateIssue: (issueId: string, updates: Partial<Issue>) => void;
  acknowledgeIssue: (issueId: string) => void;
  suppressIssue: (issueId: string, reason: string, until: string, notes: string) => void;
  closeIssue: (issueId: string) => void;
  reopenIssue: (issueId: string) => void;
  selectIssue: (issueId: string | null) => void;
  setIssueFilter: (filter: IssueFilterId) => void;

  // Task actions
  createTaskFromIssues: (issueIds: string[], overrides?: Partial<Task>) => Task;
  addTask: (task: Task) => void;
  updateTask: (taskId: string, updates: Partial<Task>) => void;
  completeTask: (taskId: string, completedBy?: string) => void;
  cancelTask: (taskId: string) => void;
  selectTask: (taskId: string | null) => void;
  setActiveTab: (tab: TasksTabId) => void;

  // Batch
  toggleBatchMode: () => void;
  toggleTaskSelection: (taskId: string) => void;
  selectAllTasks: () => void;
  clearBatchSelection: () => void;

  // Persistence
  loadTasksConfig: (config: TasksConfig | undefined) => void;
  getTasksConfig: () => TasksConfig;

  // Audit
  addAuditEntry: (target: string, targetId: string, action: string, details: string) => void;
}

// ============================================
// Store
// ============================================

export const useTaskStore = create<TaskState>((set, get) => {
  // ── V4 Entity Write Helpers ─────────────────────────────────
  // Fire-and-forget API calls. On success → update _version.
  // On 409 → reload single entity from server. On other error → rollback.

  function fireIssueCreate(issue: Issue) {
    const { _entitySiteId: siteId, _entityMigrated: migrated } = get();
    if (!siteId || !migrated) return;

    pendingIssueWrites.set(issue.issueId, null);
    createEntityIssue(siteId, issue)
      .then((created) => {
        pendingIssueWrites.delete(issue.issueId);
        set((state) => ({
          issues: state.issues.map((i) =>
            i.issueId === issue.issueId ? { ...i, _version: created._version } : i
          ),
        }));
      })
      .catch((err) => {
        console.warn('[V4] Entity issue create failed, rolling back:', err);
        set((state) => ({
          issues: state.issues.filter((i) => i.issueId !== issue.issueId),
        }));
        pendingIssueWrites.delete(issue.issueId);
      });
  }

  function fireIssueUpdate(issueId: string, updates: Partial<Issue>, preSnapshot: Issue) {
    const { _entitySiteId: siteId, _entityMigrated: migrated } = get();
    if (!siteId || !migrated || !preSnapshot._version) return;

    if (!pendingIssueWrites.has(issueId)) {
      pendingIssueWrites.set(issueId, preSnapshot);
    }
    const merged = { ...preSnapshot, ...updates };
    updateEntityIssue(siteId, issueId, updates, merged, preSnapshot._version)
      .then((updated) => {
        pendingIssueWrites.delete(issueId);
        set((state) => ({
          issues: state.issues.map((i) =>
            i.issueId === issueId ? { ...i, _version: updated._version } : i
          ),
        }));
      })
      .catch(async (err) => {
        if (isConflictError(err)) {
          console.warn('[V4 CONFLICT] Issue update conflict, reloading:', issueId);
          const server = await fetchSingleEntityIssue(siteId, issueId);
          if (server) {
            set((state) => ({
              issues: state.issues.map((i) => (i.issueId === issueId ? server : i)),
            }));
          }
        } else {
          console.warn('[V4] Entity issue update failed, rolling back:', err);
          set((state) => ({
            issues: state.issues.map((i) => (i.issueId === issueId ? preSnapshot : i)),
          }));
        }
        pendingIssueWrites.delete(issueId);
      });
  }

  function fireIssueDelete(issueId: string, preSnapshot: Issue) {
    const { _entitySiteId: siteId, _entityMigrated: migrated } = get();
    if (!siteId || !migrated || !preSnapshot._version) return;

    if (!pendingIssueWrites.has(issueId)) {
      pendingIssueWrites.set(issueId, preSnapshot);
    }
    deleteEntityIssue(siteId, issueId, preSnapshot._version)
      .then(() => {
        pendingIssueWrites.delete(issueId);
      })
      .catch(async (err) => {
        if (isConflictError(err)) {
          const server = await fetchSingleEntityIssue(siteId, issueId);
          if (server) {
            set((state) => ({ issues: [...state.issues, server] }));
          }
        } else {
          console.warn('[V4] Entity issue delete failed, rolling back:', err);
          set((state) => ({ issues: [...state.issues, preSnapshot] }));
        }
        pendingIssueWrites.delete(issueId);
      });
  }

  function fireTaskCreate(task: Task) {
    const { _entitySiteId: siteId, _entityMigrated: migrated } = get();
    if (!siteId || !migrated) return;

    pendingTaskWrites.set(task.taskId, null);
    createEntityTask(siteId, task)
      .then((created) => {
        pendingTaskWrites.delete(task.taskId);
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.taskId === task.taskId ? { ...t, _version: created._version } : t
          ),
        }));
      })
      .catch((err) => {
        console.warn('[V4] Entity task create failed, rolling back:', err);
        set((state) => ({
          tasks: state.tasks.filter((t) => t.taskId !== task.taskId),
        }));
        pendingTaskWrites.delete(task.taskId);
      });
  }

  function fireTaskUpdate(taskId: string, updates: Partial<Task>, preSnapshot: Task) {
    const { _entitySiteId: siteId, _entityMigrated: migrated } = get();
    if (!siteId || !migrated || !preSnapshot._version) return;

    if (!pendingTaskWrites.has(taskId)) {
      pendingTaskWrites.set(taskId, preSnapshot);
    }
    const merged = { ...preSnapshot, ...updates };
    updateEntityTask(siteId, taskId, updates, merged, preSnapshot._version)
      .then((updated) => {
        pendingTaskWrites.delete(taskId);
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.taskId === taskId ? { ...t, _version: updated._version } : t
          ),
        }));
      })
      .catch(async (err) => {
        if (isConflictError(err)) {
          console.warn('[V4 CONFLICT] Task update conflict, reloading:', taskId);
          const server = await fetchSingleEntityTask(siteId, taskId);
          if (server) {
            set((state) => ({
              tasks: state.tasks.map((t) => (t.taskId === taskId ? server : t)),
            }));
          }
        } else {
          console.warn('[V4] Entity task update failed, rolling back:', err);
          set((state) => ({
            tasks: state.tasks.map((t) => (t.taskId === taskId ? preSnapshot : t)),
          }));
        }
        pendingTaskWrites.delete(taskId);
      });
  }

  // ── Return store state + actions ────────────────────────────

  return {
    // Initial state
    issues: [],
    tasks: [],
    auditLog: [],
    _entitySiteId: null,
    _entityMigrated: false,
    selectedIssueId: null,
    selectedTaskId: null,
    activeTab: 'tasks',
    issueFilter: 'all',
    batchMode: false,
    selectedTaskIds: new Set(),

    setEntityAuthority: (siteId, migrated) => {
      set({ _entitySiteId: siteId, _entityMigrated: migrated });
    },

    // ── Issue Actions ─────────────────────────────────────────────────

    syncFromIndications: (indications, siteId) => {
      const { issues } = get();
      const existingByRef = new Map(issues.map((i) => [i.sourceRefId, i]));
      const newIssues: Issue[] = [];
      const updatedSnapshots: { issueId: string; updates: Partial<Issue>; pre: Issue }[] = [];

      for (const ind of indications) {
        if (['Video', 'Photo', 'DataPoint'].includes(ind.type) && !ind.properties.createIssue) continue;

        const geo = centroid(ind.geometry);
        const props = ind.properties;
        const assetPath = {
          station: props.station,
          inverter: props.inverter,
          mppt: props.mppt,
          dccb: props.dccb,
          string: props.string,
          module: props.module,
        };

        const existing = existingByRef.get(ind.id);
        if (existing) {
          const updates: Partial<Issue> = {
            assetPath,
            geo,
            indType: ind.type,
            category: indicationCategory(ind),
            severity: INDICATION_SEVERITY[ind.type] ?? 'Info',
            image: props.image,
            sourcePhotoId: props.sourcePhotoId,
            thermalData: props.thermalData,
            lastSeenAt: now(),
          };
          set((state) => ({
            issues: state.issues.map((i) => i.issueId === existing.issueId ? { ...i, ...updates } : i),
          }));
          updatedSnapshots.push({ issueId: existing.issueId, updates, pre: existing });
          continue;
        }

        const severity: IssueSeverity = INDICATION_SEVERITY[ind.type] ?? 'Info';

        const issue: Issue = {
          issueId: uuid(),
          sourceType: 'thermal_indication',
          sourceRefId: ind.id,
          siteId,
          assetPath,
          geo,
          category: indicationCategory(ind),
          severity,
          indType: ind.type,
          confidence: 0.8,
          detectedAt: props.createdAt || now(),
          lastSeenAt: props.updatedAt || now(),
          state: 'Open',
          image: props.image,
          sourcePhotoId: props.sourcePhotoId,
          thermalData: props.thermalData,
          recommendedAction: RECOMMENDED_ACTIONS[ind.type] ?? 'Inspect and assess.',
          estimatedImpact: { powerLossW: 0, energyLossKWh: 0, revenueLoss: 0 },
          notes: props.notes ?? '',
          auditLog: [{ timestamp: now(), action: 'created', user: 'system', details: `Synced from indication ${ind.type}` }],
        };

        newIssues.push(issue);
      }

      if (newIssues.length > 0) {
        set((state) => ({
          issues: [...state.issues, ...newIssues],
          auditLog: [
            ...state.auditLog,
            { timestamp: now(), action: 'sync', user: 'system', details: `Synced ${newIssues.length} new, updated ${updatedSnapshots.length} issues from indications` },
          ],
        }));
      }

      // V4: Fire entity writes for new + updated issues
      for (const issue of newIssues) fireIssueCreate(issue);
      for (const { issueId, updates, pre } of updatedSnapshots) fireIssueUpdate(issueId, updates, pre);

      return newIssues.length;
    },

    syncOrRemoveIssueForIndication: (indication, siteId, createIssue) => {
      const { issues } = get();
      const existing = issues.find((i) => i.sourceRefId === indication.id);

      if (createIssue && !existing) {
        const props = indication.properties;
        const geo = centroid(indication.geometry);
        const issue: Issue = {
          issueId: uuid(),
          sourceType: 'thermal_indication',
          sourceRefId: indication.id,
          siteId,
          assetPath: {
            station: props.station,
            inverter: props.inverter,
            mppt: props.mppt,
            dccb: props.dccb,
            string: props.string,
            module: props.module,
          },
          geo,
          category: indicationCategory(indication),
          severity: INDICATION_SEVERITY[indication.type] ?? 'Info',
          indType: indication.type,
          confidence: 0.8,
          detectedAt: props.createdAt || now(),
          lastSeenAt: now(),
          state: 'Open',
          image: props.image,
          sourcePhotoId: props.sourcePhotoId,
          thermalData: props.thermalData,
          recommendedAction: RECOMMENDED_ACTIONS[indication.type] ?? 'Inspect and assess.',
          estimatedImpact: { powerLossW: 0, energyLossKWh: 0, revenueLoss: 0 },
          notes: props.notes ?? '',
          auditLog: [{ timestamp: now(), action: 'created', user: 'system', details: `Created from indication ${indication.type}` }],
        };
        set((state) => ({ issues: [...state.issues, issue] }));
        fireIssueCreate(issue);
      } else if (!createIssue && existing) {
        set((state) => ({
          issues: state.issues.filter((i) => i.issueId !== existing.issueId),
        }));
        fireIssueDelete(existing.issueId, existing);
      }
    },

    syncFromAlarms: () => {
      const { issues } = get();
      const existingRefs = new Set(issues.map((i) => i.sourceRefId));

      const monState = useMonitoringStore.getState();
      const activeAlarms = monState.activeAlarms ?? [];
      const triggeredAdvanced: TriggeredAdvancedRule[] = Array.isArray(monState.triggeredAdvancedRules) ? monState.triggeredAdvancedRules : [];
      const datapoints = monState.projectConfig?.datapoints ?? [];
      const siteId = monState.selectedSiteId ?? '';

      const dpMap = new Map<string, DataPointConfig>();
      for (const dp of datapoints) dpMap.set(dp.id, dp);

      const newIssues: Issue[] = [];

      for (const alarm of activeAlarms) {
        if (existingRefs.has(alarm.id)) continue;

        const dp = dpMap.get(alarm.datapoint_id);
        const displayName = dp?.display_name ?? alarm.datapoint_id;
        const severity: IssueSeverity = alarm.severity === 'critical' ? 'Critical' : 'Major';
        const geo = dp?.coordinates
          ? { lng: dp.coordinates[0], lat: dp.coordinates[1] }
          : { lng: 0, lat: 0 };

        const groupLabel = dp?.group
          ? dp.group.charAt(0).toUpperCase() + dp.group.slice(1)
          : 'Monitoring';

        const issue: Issue = {
          issueId: uuid(),
          sourceType: 'monitoring_alert',
          sourceRefId: alarm.id,
          siteId,
          assetPath: {
            station: groupLabel,
            inverter: displayName,
          },
          geo,
          category: `${groupLabel} Alarm`,
          severity,
          indType: `${alarm.metric} ${alarm.operator} ${alarm.threshold}`,
          confidence: alarm.state === 'active' ? 0.95 : 0.7,
          detectedAt: alarm.triggered_at || now(),
          lastSeenAt: alarm.triggered_at || now(),
          state: alarm.state === 'acknowledged' ? 'Acknowledged' : 'Open',
          recommendedAction: alarm.message
            || `Check ${displayName}: ${alarm.metric} ${alarm.operator} ${alarm.threshold}. Current: ${alarm.actual_value ?? 'N/A'}`,
          estimatedImpact: { powerLossW: 0, energyLossKWh: 0, revenueLoss: 0 },
          notes: alarm.notes ?? '',
          auditLog: [{ timestamp: now(), action: 'created', user: 'system', details: `Synced from alarm on ${displayName}` }],
        };

        newIssues.push(issue);
      }

      for (const rule of triggeredAdvanced) {
        if (existingRefs.has(rule.rule_id)) continue;

        const severity: IssueSeverity = rule.severity === 'critical' ? 'Critical' : 'Major';
        const typeLabel = rule.rule_type === 'zero_check' ? 'Zero Check'
          : rule.rule_type === 'deviation' ? 'Deviation' : 'Expression';

        const firstMatch = rule.matched_metrics?.[0];

        const issue: Issue = {
          issueId: uuid(),
          sourceType: 'monitoring_alert',
          sourceRefId: rule.rule_id,
          siteId,
          assetPath: {
            station: typeLabel,
            inverter: firstMatch?.entity_id ?? rule.rule_name,
          },
          geo: { lng: 0, lat: 0 },
          category: `Advanced Alarm`,
          severity,
          indType: `${typeLabel}: ${rule.rule_name}`,
          confidence: 0.95,
          detectedAt: now(),
          lastSeenAt: now(),
          state: 'Open',
          recommendedAction: rule.message || rule.details,
          estimatedImpact: { powerLossW: 0, energyLossKWh: 0, revenueLoss: 0 },
          notes: '',
          advancedAlarmData: {
            ruleType: rule.rule_type,
            ruleName: rule.rule_name,
            details: rule.details,
            matchedMetrics: rule.matched_metrics,
          },
          auditLog: [{ timestamp: now(), action: 'created', user: 'system', details: `Synced from advanced rule: ${rule.rule_name}` }],
        };

        newIssues.push(issue);
      }

      if (newIssues.length > 0) {
        set((state) => ({
          issues: [...state.issues, ...newIssues],
          auditLog: [
            ...state.auditLog,
            { timestamp: now(), action: 'sync', user: 'system', details: `Synced ${newIssues.length} issues from monitoring alarms` },
          ],
        }));
        // V4: Fire entity creates
        for (const issue of newIssues) fireIssueCreate(issue);
      }

      return newIssues.length;
    },

    addIssue: (issue) => {
      set((state) => ({ issues: [...state.issues, issue] }));
      fireIssueCreate(issue);
    },

    updateIssue: (issueId, updates) => {
      const existing = get().issues.find((i) => i.issueId === issueId);
      set((state) => ({
        issues: state.issues.map((i) =>
          i.issueId === issueId ? { ...i, ...updates } : i
        ),
      }));
      if (existing) fireIssueUpdate(issueId, updates, existing);
    },

    acknowledgeIssue: (issueId) => {
      const existing = get().issues.find((i) => i.issueId === issueId);
      const ts = now();
      const updates: Partial<Issue> = {
        state: 'Acknowledged' as IssueState,
        auditLog: [...(existing?.auditLog ?? []), { timestamp: ts, action: 'acknowledged', user: 'user', details: 'Issue acknowledged' }],
      };
      set((state) => ({
        issues: state.issues.map((i) =>
          i.issueId === issueId ? { ...i, ...updates } : i
        ),
        auditLog: [...state.auditLog, { timestamp: ts, action: 'acknowledged', user: 'user', details: `Issue ${issueId} acknowledged` }],
      }));
      if (existing) fireIssueUpdate(issueId, updates, existing);
    },

    suppressIssue: (issueId, reason, until, notes) => {
      const existing = get().issues.find((i) => i.issueId === issueId);
      const ts = now();
      const updates: Partial<Issue> = {
        state: 'Suppressed' as IssueState,
        suppression: { reason, until, notes, timestamp: ts },
        auditLog: [...(existing?.auditLog ?? []), { timestamp: ts, action: 'suppressed', user: 'user', details: `Suppressed: ${reason}` }],
      };
      set((state) => ({
        issues: state.issues.map((i) =>
          i.issueId === issueId ? { ...i, ...updates } : i
        ),
        auditLog: [...state.auditLog, { timestamp: ts, action: 'suppressed', user: 'user', details: `Issue ${issueId} suppressed: ${reason}` }],
      }));
      if (existing) fireIssueUpdate(issueId, updates, existing);
    },

    closeIssue: (issueId) => {
      const existing = get().issues.find((i) => i.issueId === issueId);
      const ts = now();
      const updates: Partial<Issue> = {
        state: 'Closed' as IssueState,
        auditLog: [...(existing?.auditLog ?? []), { timestamp: ts, action: 'closed', user: 'system', details: 'Issue closed' }],
      };
      set((state) => ({
        issues: state.issues.map((i) =>
          i.issueId === issueId ? { ...i, ...updates } : i
        ),
      }));
      if (existing) fireIssueUpdate(issueId, updates, existing);
    },

    reopenIssue: (issueId) => {
      const existing = get().issues.find((i) => i.issueId === issueId);
      const ts = now();
      const updates: Partial<Issue> = {
        state: 'Open' as IssueState,
        suppression: undefined,
        auditLog: [...(existing?.auditLog ?? []), { timestamp: ts, action: 'reopened', user: 'user', details: 'Issue reopened' }],
      };
      set((state) => ({
        issues: state.issues.map((i) =>
          i.issueId === issueId ? { ...i, ...updates } : i
        ),
        auditLog: [...state.auditLog, { timestamp: ts, action: 'reopened', user: 'user', details: `Issue ${issueId} reopened` }],
      }));
      if (existing) fireIssueUpdate(issueId, updates, existing);
    },

    selectIssue: (issueId) => set({ selectedIssueId: issueId }),

    setIssueFilter: (filter) => set({ issueFilter: filter }),

    // ── Task Actions ──────────────────────────────────────────────────

    createTaskFromIssues: (issueIds, overrides) => {
      const { issues } = get();
      const linked = issues.filter((i) => issueIds.includes(i.issueId));
      const ts = now();

      const totalImpact: EstimatedImpact = { powerLossW: 0, energyLossKWh: 0, revenueLoss: 0 };
      for (const issue of linked) {
        totalImpact.powerLossW += issue.estimatedImpact.powerLossW;
        totalImpact.energyLossKWh += issue.estimatedImpact.energyLossKWh;
        totalImpact.revenueLoss += issue.estimatedImpact.revenueLoss;
      }

      const severities: IssueSeverity[] = linked.map((i) => i.severity);
      const highestSeverity: IssueSeverity =
        severities.includes('Critical') ? 'Critical'
        : severities.includes('Major') ? 'Major'
        : severities.includes('Minor') ? 'Minor'
        : 'Info';
      const priority: TaskPriority = SEVERITY_PRIORITY[highestSeverity];

      const categories = [...new Set(linked.map((i) => i.category))];
      const autoTitle = linked.length === 1
        ? `${linked[0].category}: ${linked[0].indType} at ${linked[0].assetPath.station || 'site'}`
        : `${categories.join(', ')} (${linked.length} issues)`;

      const task: Task = {
        taskId: uuid(),
        title: autoTitle,
        description: linked.map((i) => i.recommendedAction).join('\n'),
        linkedIssueIds: issueIds,
        priority,
        status: 'Draft',
        assignees: [],
        sla: {},
        schedule: {},
        estimatedImpact: totalImpact,
        checklist: [],
        auditLog: [{ timestamp: ts, action: 'created', user: 'user', details: `Task created from ${issueIds.length} issue(s)` }],
        ...overrides,
      };

      // Capture pre-snapshots for linked issues before mutation
      const linkedSnapshots = linked.map((i) => ({ ...i }));

      set((state) => ({
        tasks: [...state.tasks, task],
        issues: state.issues.map((i) =>
          issueIds.includes(i.issueId)
            ? {
                ...i,
                state: 'ConvertedToTask' as IssueState,
                auditLog: [...i.auditLog, { timestamp: ts, action: 'converted', user: 'user', details: `Converted to task ${task.taskId}` }],
              }
            : i
        ),
        auditLog: [
          ...state.auditLog,
          { timestamp: ts, action: 'task_created', user: 'user', details: `Task "${task.title}" created from ${issueIds.length} issue(s)` },
        ],
      }));

      // V4: Fire entity writes — task create + issue state updates
      fireTaskCreate(task);
      for (const snap of linkedSnapshots) {
        const updates: Partial<Issue> = {
          state: 'ConvertedToTask' as IssueState,
          auditLog: [...snap.auditLog, { timestamp: ts, action: 'converted', user: 'user', details: `Converted to task ${task.taskId}` }],
        };
        fireIssueUpdate(snap.issueId, updates, snap);
      }

      return task;
    },

    addTask: (task) => {
      set((state) => ({ tasks: [...state.tasks, task] }));
      fireTaskCreate(task);
    },

    updateTask: (taskId, updates) => {
      const existing = get().tasks.find((t) => t.taskId === taskId);
      set((state) => ({
        tasks: state.tasks.map((t) =>
          t.taskId === taskId ? { ...t, ...updates } : t
        ),
      }));
      if (existing) fireTaskUpdate(taskId, updates, existing);
    },

    completeTask: (taskId, completedBy = 'user') => {
      const ts = now();
      const { tasks, issues } = get();
      const task = tasks.find((t) => t.taskId === taskId);
      if (!task) return;

      const taskUpdates: Partial<Task> = {
        status: 'Done' as TaskStatus,
        workReport: { completedBy, completedAt: ts },
        auditLog: [...task.auditLog, { timestamp: ts, action: 'completed', user: completedBy, details: 'Task completed' }],
      };

      // Capture linked issue snapshots before mutation
      const linkedIssues = issues.filter(
        (i) => task.linkedIssueIds.includes(i.issueId) && i.state !== 'Closed'
      );
      const linkedSnapshots = linkedIssues.map((i) => ({ ...i }));

      set((state) => ({
        tasks: state.tasks.map((t) =>
          t.taskId === taskId ? { ...t, ...taskUpdates } : t
        ),
        issues: state.issues.map((i) =>
          task.linkedIssueIds.includes(i.issueId) && i.state !== 'Closed'
            ? {
                ...i,
                state: 'Closed' as IssueState,
                auditLog: [...i.auditLog, { timestamp: ts, action: 'closed', user: 'system', details: `Closed by task ${taskId} completion` }],
              }
            : i
        ),
        auditLog: [
          ...state.auditLog,
          { timestamp: ts, action: 'task_completed', user: completedBy, details: `Task "${task.title}" completed` },
        ],
      }));

      // V4: Fire entity writes — task update + issue close updates
      fireTaskUpdate(taskId, taskUpdates, task);
      for (const snap of linkedSnapshots) {
        const updates: Partial<Issue> = {
          state: 'Closed' as IssueState,
          auditLog: [...snap.auditLog, { timestamp: ts, action: 'closed', user: 'system', details: `Closed by task ${taskId} completion` }],
        };
        fireIssueUpdate(snap.issueId, updates, snap);
      }
    },

    cancelTask: (taskId) => {
      const ts = now();
      const { tasks, issues } = get();
      const task = tasks.find((t) => t.taskId === taskId);
      if (!task) return;

      const taskUpdates: Partial<Task> = {
        status: 'Canceled' as TaskStatus,
        auditLog: [...task.auditLog, { timestamp: ts, action: 'canceled', user: 'user', details: 'Task canceled' }],
      };

      // Capture linked issue snapshots before mutation
      const linkedIssues = issues.filter(
        (i) => task.linkedIssueIds.includes(i.issueId) && i.state === 'ConvertedToTask'
      );
      const linkedSnapshots = linkedIssues.map((i) => ({ ...i }));

      set((state) => ({
        tasks: state.tasks.map((t) =>
          t.taskId === taskId ? { ...t, ...taskUpdates } : t
        ),
        issues: state.issues.map((i) =>
          task.linkedIssueIds.includes(i.issueId) && i.state === 'ConvertedToTask'
            ? {
                ...i,
                state: 'Open' as IssueState,
                auditLog: [...i.auditLog, { timestamp: ts, action: 'reopened', user: 'system', details: `Reopened after task ${taskId} canceled` }],
              }
            : i
        ),
        auditLog: [
          ...state.auditLog,
          { timestamp: ts, action: 'task_canceled', user: 'user', details: `Task "${task.title}" canceled` },
        ],
      }));

      // V4: Fire entity writes — task update + issue reopen updates
      fireTaskUpdate(taskId, taskUpdates, task);
      for (const snap of linkedSnapshots) {
        const updates: Partial<Issue> = {
          state: 'Open' as IssueState,
          auditLog: [...snap.auditLog, { timestamp: ts, action: 'reopened', user: 'system', details: `Reopened after task ${taskId} canceled` }],
        };
        fireIssueUpdate(snap.issueId, updates, snap);
      }
    },

    selectTask: (taskId) => set({ selectedTaskId: taskId }),

    setActiveTab: (tab) => set({ activeTab: tab }),

    // ── Batch ─────────────────────────────────────────────────────────

    toggleBatchMode: () =>
      set((state) => ({
        batchMode: !state.batchMode,
        selectedTaskIds: state.batchMode ? new Set() : state.selectedTaskIds,
      })),

    toggleTaskSelection: (taskId) =>
      set((state) => {
        const next = new Set(state.selectedTaskIds);
        if (next.has(taskId)) next.delete(taskId);
        else next.add(taskId);
        return { selectedTaskIds: next };
      }),

    selectAllTasks: () => {
      const { tasks, activeTab } = get();
      const visible = activeTab === 'completed'
        ? tasks.filter((t) => t.status === 'Done')
        : tasks.filter((t) => t.status !== 'Done' && t.status !== 'Canceled');
      set({ selectedTaskIds: new Set(visible.map((t) => t.taskId)) });
    },

    clearBatchSelection: () => set({ selectedTaskIds: new Set() }),

    // ── Persistence ───────────────────────────────────────────────────

    loadTasksConfig: (config) => {
      if (!config) {
        set({ issues: [], tasks: [], auditLog: [], _entitySiteId: null, _entityMigrated: false });
        return;
      }
      // Reset entity authority — caller (loadProjectFromServer) will
      // set it via setEntityAuthority() after loadProject() completes.
      set({
        issues: config.issues ?? [],
        tasks: config.tasks ?? [],
        auditLog: config.auditLog ?? [],
        _entitySiteId: null,
        _entityMigrated: false,
      });
    },

    getTasksConfig: () => {
      const { issues, tasks, auditLog } = get();
      // V4: Return confirmed-only state. Pending entity writes are
      // replaced with pre-mutation snapshots so blob autosave never
      // serializes unconfirmed optimistic mutations.
      return {
        issues: getConfirmedIssues(issues),
        tasks: getConfirmedTasks(tasks),
        auditLog,
      };
    },

    // ── Audit ─────────────────────────────────────────────────────────

    addAuditEntry: (target, targetId, action, details) => {
      const entry: AuditEntry = { timestamp: now(), action, user: 'user', details: `[${target}:${targetId}] ${details}` };
      set((state) => ({ auditLog: [...state.auditLog, entry] }));
    },
  };
});
